/**
 * Icon lookup helpers for Connected apps (Merge + Nango).
 * Kept out of the .tsx component file for react-refresh.
 */

import type { LucideIcon } from 'lucide-react';
import {
  Activity,
  Box,
  Building2,
  Calendar,
  Cloud,
  FileSignature,
  FileSpreadsheet,
  FileText,
  FolderOpen,
  HardDrive,
  HeartPulse,
  KanbanSquare,
  Mail,
  MessageSquare,
  NotebookPen,
  Stethoscope,
  Video,
} from 'lucide-react';

const DEFAULT_ICON = Cloud;

/** Normalize Merge underscores / Nango hyphens / aliases to one lookup key. */
export function normalizeConnectedAppIconKey(id: string): string {
  const key = id.trim().toLowerCase().replaceAll('_', '-');
  if (key === 'gmail' || key === 'google-mail') return 'gmail';
  if (key === 'google-sheet' || key === 'google-sheets') return 'google-sheets';
  if (key === 'one-note' || key === 'onenote') return 'onenote';
  if (key === 'microsoft-teams' || key === 'ms-teams') return 'microsoft-teams';
  if (key === 'docu-sign' || key === 'docusign') return 'docusign';
  if (key === 'share-point' || key === 'sharepoint-online') return 'sharepoint';
  if (key === 'one-drive' || key === 'onedrive') return 'one-drive';
  if (key === 'bio-rxiv' || key === 'biorxiv') return 'bio-rxiv';
  return key;
}

const ICON_BY_KEY: Record<string, LucideIcon> = {
  gmail: Mail,
  outlook: Mail,
  microsoft: Mail,
  'google-calendar': Calendar,
  'google-drive': HardDrive,
  'google-docs': FileText,
  'google-sheets': FileSpreadsheet,
  'google-meet': Video,
  'microsoft-teams': MessageSquare,
  'microsoft-word': FileText,
  'microsoft-excel': FileSpreadsheet,
  onenote: NotebookPen,
  dropbox: Cloud,
  box: Box,
  'one-drive': HardDrive,
  sharepoint: FolderOpen,
  slack: MessageSquare,
  notion: NotebookPen,
  zoom: Video,
  linear: KanbanSquare,
  hubspot: Building2,
  salesforce: Building2,
  docusign: FileSignature,
  'dropbox-sign': FileSignature,
  clinicaltrials: Stethoscope,
  'cms-coverage': Stethoscope,
  'npi-registry': Stethoscope,
  'bio-rxiv': Stethoscope,
  pubmed: Stethoscope,
  'adobe-pdf-services': FileText,
  drchrono: Stethoscope,
  practicefusion: Stethoscope,
  modmed: Stethoscope,
  dentally: Stethoscope,
  'health-gorilla': Stethoscope,
  availity: Stethoscope,
  'veeva-vault': FolderOpen,
  oura: HeartPulse,
  whoop: Activity,
};

export function getConnectedAppLucideIcon(id: string): LucideIcon {
  return ICON_BY_KEY[normalizeConnectedAppIconKey(id)] ?? DEFAULT_ICON;
}

/** Host is allowlisted for `img-src` in `config/csp/nango-csp.js`. */
const NANGO_LOGO_BASE = 'https://app.nango.dev/images/template-logos';

/**
 * Nango's public template-logo slug per connector family. Merge and Nango share
 * one entry because both stacks front the same third-party app.
 *
 * Absent keys (PubMed, ClinicalTrials.gov, bioRxiv, NPI Registry, CMS Coverage)
 * are public data sources Nango has no artwork for — they keep the Lucide glyph.
 */
const LOGO_SLUG_BY_KEY: Record<string, string> = {
  gmail: 'google-mail',
  outlook: 'outlook',
  microsoft: 'microsoft',
  'google-calendar': 'google-calendar',
  'google-drive': 'google-drive',
  'google-docs': 'google-docs',
  'google-sheets': 'google-sheet',
  'google-meet': 'google-meet',
  'microsoft-teams': 'microsoft-teams',
  'microsoft-word': 'microsoft-word',
  'microsoft-excel': 'microsoft-excel',
  onenote: 'one-note',
  dropbox: 'dropbox',
  box: 'box',
  'one-drive': 'one-drive',
  sharepoint: 'sharepoint-online',
  slack: 'slack',
  notion: 'notion',
  zoom: 'zoom',
  linear: 'linear',
  hubspot: 'hubspot',
  salesforce: 'salesforce',
  myob: 'myob',
  xero: 'xero',
  docusign: 'docusign',
  'dropbox-sign': 'dropbox-sign',
  drchrono: 'drchrono',
  practicefusion: 'practicefusion',
  modmed: 'modmed',
  dentally: 'dentally',
  'health-gorilla': 'health-gorilla',
  availity: 'availity',
  'veeva-vault': 'veeva-vault',
  oura: 'oura',
  whoop: 'whoop',
  'adobe-pdf-services': 'adobe',
};

/**
 * Brand logo for a connector, or `undefined` when we only have a Lucide glyph.
 *
 * The host is unreachable on-prem (airgapped `img-src` excludes it), so callers
 * must treat a load failure as "fall back to the Lucide icon" rather than an error.
 */
export function getConnectedAppLogoUrl(id: string): string | undefined {
  const slug = LOGO_SLUG_BY_KEY[normalizeConnectedAppIconKey(id)];
  return slug ? `${NANGO_LOGO_BASE}/${slug}.svg` : undefined;
}
